import axios, { AxiosError } from "axios"

// ── Types ──────────────────────────────────────────────────────────────

export interface PaymentsClient {
  id: string
  first_name: string
  last_name: string
  email: string
  company_name: string
  address_line1?: string
  city?: string
  state?: string
  zip?: string
  country?: string
  /** Legal name on file (from the ESO client master); null when there is none. */
  legal_name?: string | null
}

export interface BankAccountIdentityCheck {
  status: "match" | "mismatch"
  match: boolean
  mismatches: string[] // any of "name" | "email" | "phone"
  checkedAt: string
  plaidNames: string[]
  plaidEmails: string[]
  plaidPhones: string[]
}

export interface PaymentsBankAccount {
  id: string
  client_id: string
  account_holder_name: string
  bank_name: string
  transfer_type: "ACH" | "WIRE"
  account_label?: string
  account_type?: string
  bank_country?: string
  swift_bic_code?: string
  plaid_account_id?: string
  is_active?: boolean
  is_verified?: boolean
  created_at?: string
  identity_check?: BankAccountIdentityCheck | null
  // Plaid's last-4 for the account (Plaid-connected rows only).
  plaid_mask?: string | null
  // How payments to this account will actually go, decided by the payments
  // server's rail logic (not the stored transfer_type). wire_upgrade_available
  // false = ACH only, which is when the Payment Center offers "Receive by Wire
  // Instead"; null = the server couldn't tell, so offer nothing.
  effective_rail?: "ACH" | "WIRE" | null
  wire_upgrade_available?: boolean | null
  wire_unavailable_reason?: "ach_only_routing" | "tokenized_account" | null
}

export interface CreatePaymentsClientPayload {
  first_name: string
  last_name: string
  email: string
  company_name: string
  client_number: string
  address_line1?: string
  city?: string
  state?: string
  zip?: string
  country?: string
}

export interface PlaidPaymentPayload {
  client_id: string
  public_token: string
  account_id: string
  amount: number
  currency?: string
  effective_date?: string
  description?: string
  notes?: string
  deal_eso_id?: number
  // The client confirmed that changing their bank account cancels their payment that is
  // waiting for approval and sends it again for approval to the new account (Bill,
  // 2026-10-13). Without it the payments server refuses with CONFIRM_CANCEL_PENDING.
  confirm_cancel_pending?: boolean
}

export interface ManualPaymentPayload {
  // See PlaidPaymentPayload.confirm_cancel_pending.
  confirm_cancel_pending?: boolean
  client_id: string
  deal_eso_id?: number
  transfer_type: "ACH" | "WIRE"
  account_holder_name: string
  bank_name: string
  amount: number
  bank_country?: string
  account_number?: string
  routing_number?: string
  iban?: string
  swift_bic_code?: string
  // US-dollar correspondent bank ABA for international (SWIFT) wires. The payments
  // server auto-resolves this from a curated BIC table and hard-fails the payment
  // when it cannot — this lets the client supply it for an unlisted bank.
  correspondent_bank_aba?: string
  currency?: string
  effective_date?: string
  description?: string
  notes?: string
  account_type?: string
  account_label?: string
  bank_address?: string
  save_only?: boolean
  deal_manager_email?: string
  // "Receive Payments by Wire" form: refuse a routing number that can't receive a
  // wire (the whole point is to stop being paid by ACH).
  require_wire?: boolean
}

export interface PaymentsTransaction {
  id: string
  transaction_number?: string
  status: string
  amount: number
  currency: string
  transfer_type: "ACH" | "WIRE"
  client_id: string
  bank_account_id: string
  approved_by?: string
  effective_date?: string
  description?: string
}

export interface IdentityCheckResult {
  match: boolean
  // List of fields that didn't match — possible values: "name", "email", "phone"
  mismatches: string[]
  clientPhoneLast4?: string | null
}

export interface PaymentResult {
  transaction?: PaymentsTransaction
  bank_account?: { id: string; [k: string]: unknown }
  bank_account_id?: string
  message?: string
  identity_check?: IdentityCheckResult | null
  // Set when connecting a new account re-pointed an in-flight pending payment to
  // it (server-side replace semantics) — no new transaction was created.
  repointed_transaction?: string
  // The change cancelled the client's payment that was waiting for approval and sent it
  // again for approval to the new account (`transaction` is the new one).
  cancelled_transaction?: string
  // The amount this change carried was not submitted: another payment is waiting for
  // approval, and this one is submitted automatically once that one is approved.
  payment_held?: { reason: string; waiting_transaction?: string; message: string }
  // A re-link of the same account: the payment it carried was already waiting.
  existing_transaction?: string
}

// What changing the client's bank account would do to their payments in flight, asked
// before the change (Bill, 2026-10-13): `waiting` is cancelled and sent again for approval
// to the new account once the client confirms; `in_process` blocks the change until it
// is released. Company payments are never part of it.
export interface AccountChangeCheck {
  waiting: { transaction_number: string; amount: number; currency: string } | null
  in_process: { transaction_number: string; amount: number; currency: string; status: string } | null
}

export interface PlaidBalanceAccount {
  plaid_account_id: string
  name: string
  available: number | null
  current: number | null
  currency: string
}

export class PaymentsApiError extends Error {
  status: number
  details?: unknown
  // The payments server's refusal code (e.g. CONFIRM_CANCEL_PENDING, PAYMENT_IN_PROCESS).
  code?: string

  constructor(status: number, message: string, details?: unknown, code?: string) {
    super(message)
    this.name = "PaymentsApiError"
    this.status = status
    this.details = details
    this.code = code
  }
}

type ApiEnvelope<T> = {
  data: T
  message?: string
}

// ── Axios instance ─────────────────────────────────────────────────────
// All payment requests go through the portal backend proxy at /payments/*.
// The backend handles service auth with the payments server — no secrets
// are exposed to the browser.

const portalBaseUrl = process.env.REACT_APP_SERVER_URL?.trim() || "http://localhost:8080"

const paymentsAxios = axios.create({
  baseURL: portalBaseUrl + "/payments",
  headers: { "Content-Type": "application/json" },
  withCredentials: true, // send portal session cookie
})

// Handle errors with structured PaymentsApiError
paymentsAxios.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ error?: string; message?: string; details?: unknown; code?: string }>) => {
    const status = error.response?.status ?? 0
    const body = error.response?.data

    if (status === 401) {
      return Promise.reject(new PaymentsApiError(401, "Session expired. Please log in again."))
    }
    if (status === 403) {
      // Surface the server's actual message (e.g. Plaid identity mismatch) when available.
      const msg = body?.error || body?.message || "You are not authorized to perform this action."
      return Promise.reject(new PaymentsApiError(403, msg, body?.details))
    }
    if (status === 400) {
      const msg = body?.error || body?.message || "Invalid request. Please check your input."
      return Promise.reject(new PaymentsApiError(400, msg, body?.details))
    }
    if (status === 409) {
      const msg = body?.error || body?.message || "Conflict."
      return Promise.reject(new PaymentsApiError(409, msg, body?.details, typeof body?.code === "string" ? body.code : undefined))
    }
    if (status === 429) {
      const msg = body?.error || "Too many requests. Please wait a little and try again."
      return Promise.reject(new PaymentsApiError(429, msg))
    }
    if (status >= 500) {
      return Promise.reject(new PaymentsApiError(status, "Server error. Please try again later."))
    }
    return Promise.reject(
      new PaymentsApiError(status || 0, error.message || "Network error. Please check your connection.")
    )
  }
)

// ── Plaid ───────────────────────────────────────────────────────────────

export const createPlaidLinkToken = async (payload: { client_id: string; phone_number?: string }) => {
  const response = await paymentsAxios.post<ApiEnvelope<{ link_token: string; expiration: string }>>(
    "/plaid/link-token",
    payload,
  )
  return response.data.data
}

export const submitPlaidPayment = async (payload: PlaidPaymentPayload) => {
  const response = await paymentsAxios.post<ApiEnvelope<PaymentResult>>(
    "/plaid/payment",
    payload,
  )
  return response.data.data
}

export const getPlaidBalance = async (accountId: string) => {
  const response = await paymentsAxios.get<ApiEnvelope<PlaidBalanceAccount[]>>(
    `/plaid/balance/${accountId}`,
  )
  return response.data.data
}

// ── Send pending payment with existing bank account ─────────────────────

export interface SendPendingPayload {
  client_id: string
  bank_account_id: string
  amount: number
  deal_eso_id?: number
  description?: string
}

export const sendPendingPayment = async (payload: SendPendingPayload) => {
  const response = await paymentsAxios.post<ApiEnvelope<PaymentResult>>(
    "/send-pending",
    payload,
  )
  return response.data.data
}

// ── Manual payments ─────────────────────────────────────────────────────

export const submitManualPayment = async (payload: ManualPaymentPayload) => {
  const response = await paymentsAxios.post<ApiEnvelope<PaymentResult>>(
    "/manual",
    payload,
  )
  return response.data.data
}

// ── Client management ──────────────────────────────────────────────────

export const getPaymentsClientByEmail = async (email: string): Promise<PaymentsClient | null> => {
  try {
    const response = await paymentsAxios.get<ApiEnvelope<PaymentsClient>>("/clients", {
      params: { email },
    })
    return response.data.data
  } catch (error) {
    if (error instanceof PaymentsApiError && error.status === 404) {
      return null
    }
    throw error
  }
}

export const createPaymentsClient = async (payload: CreatePaymentsClientPayload) => {
  const response = await paymentsAxios.post<ApiEnvelope<PaymentsClient>>("/clients", payload)
  return response.data.data
}

export const updatePaymentsClient = async (clientId: string, payload: Partial<CreatePaymentsClientPayload>) => {
  const response = await paymentsAxios.put<ApiEnvelope<PaymentsClient>>(`/clients/${clientId}`, payload)
  return response.data.data
}

export const deletePaymentsClient = async (clientId: string) => {
  const response = await paymentsAxios.delete<ApiEnvelope<{ deleted: boolean; client_id: string }>>(`/clients/${clientId}`)
  return response.data.data
}

export const disconnectPaymentsBank = async (clientId: string, dealEsoId?: number) => {
  // deal_eso_id is how the backend resolves which DM to notify. Without it the
  // "Bank Disconnected" mail falls back to the catch-all address and the client's
  // own equity partner never hears that their payout target was removed.
  const response = await paymentsAxios.post<ApiEnvelope<{ disconnected: boolean; client_id: string; accounts_deactivated: number }>>(
    `/clients/${clientId}/disconnect-bank`,
    { deal_eso_id: dealEsoId },
  )
  return response.data.data
}

// "Receive Payments by Wire": the client's connected account can only be paid by
// ACH (a Plaid limitation), so they open the manual bank form to enter their
// bank's wire instructions. The backend checks the account really is ACH-only,
// flips the Client Override switch and emails their equity partner.
export const requestWireEntry = async (clientId: string) => {
  const response = await paymentsAxios.post<{ success: boolean; manualEntryEnabled: number; alreadyEnabled?: boolean }>(
    `/clients/${clientId}/request-wire-entry`,
  )
  return response.data
}

// "Cancel and Keep ACH": close a wire entry the client opened. Only turns the
// switch back off; the connected ACH account was never touched.
export const cancelWireEntry = async (clientId: string) => {
  const response = await paymentsAxios.post<{ success: boolean; manualEntryEnabled: number }>(
    `/clients/${clientId}/cancel-wire-entry`,
  )
  return response.data
}

export const clearPaymentsLedger = async (clientId: string) => {
  const response = await paymentsAxios.delete<ApiEnvelope<{ cleared: boolean; client_id: string; transactions_deleted: number; logs_deleted: number }>>(`/clients/${clientId}/ledger`)
  return response.data.data
}

export const listPaymentsBankAccounts = async (clientId: string) => {
  const response = await paymentsAxios.get<ApiEnvelope<PaymentsBankAccount[]>>("/bank-accounts", {
    params: { client_id: clientId },
  })
  return response.data.data
}

// ── Plaid item account selection ────────────────────────────────────────
// A Plaid connection (item) can expose several accounts even though only one
// receives payments. These back the Payment Center's account selector: list
// what's on the item (labels + masks only), and switch the payment target
// without a new Plaid Link session.

export interface PlaidItemAccount {
  plaid_account_id: string
  name: string
  official_name: string | null
  account_type: string | null
  mask: string | null
  // false = Plaid didn't return routing/account numbers for it — can't be paid
  payable: boolean
  is_current: boolean
}

export interface PlaidItemAccountsResult {
  accounts: PlaidItemAccount[]
  current_plaid_account_id: string | null
  item_error?: boolean
}

export const listPlaidItemAccounts = async (clientId: string): Promise<PlaidItemAccountsResult> => {
  const response = await paymentsAxios.get<ApiEnvelope<PlaidItemAccountsResult>>(
    "/bank-accounts/item-accounts",
    { params: { client_id: clientId } },
  )
  return response.data.data
}

export const selectPlaidItemAccount = async (payload: {
  client_id: string
  plaid_account_id: string
  deal_eso_id?: number
  // See PlaidPaymentPayload.confirm_cancel_pending.
  confirm_cancel_pending?: boolean
}) => {
  const response = await paymentsAxios.post<ApiEnvelope<{ bank_account: PaymentsBankAccount; switched: boolean; message?: string; cancelled_transaction?: string }>>(
    "/bank-accounts/select-account",
    payload,
  )
  return response.data.data
}

// Asked before a bank-account change. null when it cannot be answered (an older server,
// a network blip): the caller then goes ahead as before and the payments server, which
// re-checks under lock, refuses anything it must.
export const getAccountChangeCheck = async (clientId: string): Promise<AccountChangeCheck | null> => {
  try {
    const response = await paymentsAxios.get<ApiEnvelope<AccountChangeCheck>>(`/clients/${encodeURIComponent(clientId)}/account-change-check`)
    const data = response.data?.data
    return data && typeof data === "object" ? { waiting: data.waiting ?? null, in_process: data.in_process ?? null } : null
  } catch {
    return null
  }
}

// ── Cash flows & payment history ─────────────────────────────────────

export interface CashFlow {
  id: number
  deal_id: number
  date: string
  amount: number
  memo: string | null
}

export interface PaymentRecord {
  id: string
  client_id: string
  amount: number
  status: string
  type: string
  transfer_type: string
  memo: string | null
  bank_name: string | null
  created_at: string
  submitted_at: string | null
  approved_at: string | null
  completed_at: string | null
  released_at: string | null
}

export const getPaymentsByClientId = async (clientId: string): Promise<PaymentRecord[]> => {
  try {
    const response = await paymentsAxios.get<ApiEnvelope<PaymentRecord[]>>("/list", {
      params: { client_id: clientId },
    })
    return response.data.data
  } catch (error) {
    console.error("[payments] Failed to fetch payments for client", clientId, error)
    return []
  }
}

// ── DM (view-as-client) payment submission ──────────────────────────────
// Backed by portal-backend /deals/dm/* — deal-manager sessions only; mirrors
// the OG Set Pending Liquidity path (same staging invariants + auto-submit
// chain + payments-server caps).

const dealsAxios = axios.create({
  baseURL: portalBaseUrl + "/deals",
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
})

export interface DmDealAmounts {
  esoId: number
  pendingAmount: number | null
  pendingDescription: string | null
  totalAmount: number | null
  sentAmount: number | null
  outstandingAmount: number | null
  // Set while payments are on hold for the deal: every submission is refused (409).
  paymentHoldAt?: string | null
  paymentHoldReason?: string | null
  client?: { email: string; esoId: number | null }
}

export interface DmDailyTotal {
  date: string
  released_total: number
  pending_total: number
  running_total: number
  daily_limit: number
  available_remaining: number
  effective_is_today: boolean
  minutes_to_cutoff: number | null
}

export interface DmAutoSubmitResult {
  attempted: boolean
  submitted: boolean
  reason: string
  transactionNumber?: string
  // Sent to the deal's company account on the client's behalf (never staged), or to the
  // account the DM picked (Bill, 2026-10-15).
  toCompany?: boolean
  payee?: { accountId: string; kind: DmPaymentAccountKind; name: string | null; bankName: string | null; last4: string | null }
}

export type DmPaymentType = "exercise" | "liquidity"
export type DmPaymentAccountKind = "connected" | "dm_added" | "company"

// An account the DM can pick for a payment on the deal (portal-backend
// /deals/dm/payment-accounts; the payments server decides the list): the client's connected
// account, another of theirs a DM added, or the deal's company account, which takes the
// exercise and never the liquidity unless Bill excepted the deal. Last 4 digits only.
export interface DmPaymentAccount {
  id: string
  kind: DmPaymentAccountKind
  account_holder_name: string | null
  bank_name: string | null
  last4: string | null
  is_default: boolean
}

export const getDmPaymentAccounts = async (esoId: number, paymentType: DmPaymentType): Promise<{ defaultAccountId: string | null; accounts: DmPaymentAccount[] }> => {
  const response = await dealsAxios.get<{ success: boolean; defaultAccountId: string | null; accounts: DmPaymentAccount[] }>(
    "/dm/payment-accounts",
    { params: { esoId, paymentType } },
  )
  return { defaultAccountId: response.data.defaultAccountId ?? null, accounts: response.data.accounts ?? [] }
}

// The IRR ledger's view of a deal (portal-backend /deals/dm/irr-reference, from eso_api), for the
// DM-only reference panel (Bill, 2026-10-16: never shown to the client). Cash flows are signed as
// the IRR books them: negative is money ESO paid out, positive came back.
export interface IrrReferenceDeal {
  inceptionDate: string | null
  closingDate: string | null
  fund: string | null
  initialPayment: number | null
  additionalPayment: number | null
  totalPayment: number | null
  pp: number | null
  lp: number | null
  lpMultiple: number | null
  totalRealizations: number | null
  residualValue: number | null
  totalValue: number | null
  exitDate: string | null
  fullyExited: string | null
  asOf: string | null
}

export interface IrrReferenceCashFlow {
  date: string | null
  amount: number | null
  memo: string | null
  fund: string | null
}

export interface IrrReference {
  inIrr: boolean
  deal: IrrReferenceDeal | null
  cashFlows: IrrReferenceCashFlow[]
}

export const getDmIrrReference = async (esoId: number): Promise<IrrReference> => {
  const response = await dealsAxios.get<{ success: boolean } & IrrReference>("/dm/irr-reference", { params: { esoId } })
  return { inIrr: response.data.inIrr === true, deal: response.data.deal ?? null, cashFlows: response.data.cashFlows ?? [] }
}

export const getDmPaymentContext = async (esoId: number): Promise<{ deal: DmDealAmounts; dailyTotal: DmDailyTotal | null }> => {
  const response = await dealsAxios.get<{ success: boolean; deal: DmDealAmounts; dailyTotal: DmDailyTotal | null }>(
    "/dm/payment-context",
    { params: { esoId } },
  )
  return { deal: response.data.deal, dailyTotal: response.data.dailyTotal }
}

export const dmSubmitPayment = async (payload: { esoId: number; pendingAmount?: number; description?: string; paymentType?: DmPaymentType; bankAccountId?: string }): Promise<DmAutoSubmitResult | undefined> => {
  const response = await dealsAxios.post<{ success: boolean; autoSubmit?: DmAutoSubmitResult; error?: string }>(
    "/dm/submit-payment",
    payload,
  )
  return response.data.autoSubmit
}

export const getCashFlowsByDealId = async (dealId: string): Promise<CashFlow[]> => {
  try {
    const response = await paymentsAxios.get<ApiEnvelope<CashFlow[]>>(`/cash-flows/${dealId}`)
    return response.data.data
  } catch (error) {
    console.error("[payments] Failed to fetch cash flows for deal", dealId, error)
    return []
  }
}
